import { Link } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { ArrowLeft, Loader2, Trash2, UserPlus } from "lucide-react";

import { EmptyState, ErrorBanner, PageHeader } from "@/components/common/FeatureShell";
import { RouteErrorBoundary, routeErrorMessage } from "@/components/common/RouteErrorBoundary";
import { OrgMembersSectionSkeleton } from "@/components/skeletons/OrgMembersSkeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrentOrg } from "@/contexts/OrgContext";
import { useOrgInviteMutation, useOrgMemberRemoveMutation, useOrgMemberUpdateMutation, useSuspenseOrgMembers } from "@/hooks/queries/useOrgs";
import type { OrgMembershipRow, OrgRole } from "@/lib/api";

const ASSIGNABLE_ROLES: OrgRole[] = ["owner", "admin", "member", "viewer"];
const INVITE_ROLES: Array<"member" | "admin" | "viewer"> = ["member", "admin", "viewer"];

export default function OrgMembersPage() {
  const { currentOrg } = useCurrentOrg();
  const orgId = currentOrg?.id ?? "";
  const canManage = currentOrg?.role === "owner" || currentOrg?.role === "admin";

  const inviteMutation = useOrgInviteMutation(orgId);
  const updateMutation = useOrgMemberUpdateMutation(orgId);
  const removeMutation = useOrgMemberRemoveMutation(orgId);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin" | "viewer">("member");
  const [error, setError] = useState<string | null>(null);

  if (!currentOrg) {
    return (
      <div className="min-h-screen">
        <PageHeader title="Members" />
        <main className="max-w-3xl p-4 md:p-6">
          <EmptyState
            title="No organization selected"
            description="Pick an organization from the workspace switcher to manage its members."
            action={<Button asChild variant="outline"><Link to="/settings/organization">Go to organizations</Link></Button>}
          />
        </main>
      </div>
    );
  }

  const invite = async () => {
    setError(null);
    try {
      await inviteMutation.mutateAsync({ email: inviteEmail.trim(), role: inviteRole });
      setInviteEmail("");
    } catch (inviteError) {
      setError(inviteError instanceof Error ? inviteError.message : "Invite could not be sent.");
    }
  };

  const changeRole = async (membershipId: string, role: OrgRole) => {
    setError(null);
    try {
      await updateMutation.mutateAsync({ membershipId, role });
    } catch (roleError) {
      setError(roleError instanceof Error ? roleError.message : "Role could not be updated.");
    }
  };

  const remove = async (membershipId: string) => {
    setError(null);
    try {
      await removeMutation.mutateAsync(membershipId);
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : "Member could not be removed.");
    }
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title={`${currentOrg.name} — Members`}
        description="Invite teammates and manage roles within this organization."
        actions={<Button type="button" variant="outline" asChild><Link to="/settings/organization"><ArrowLeft className="size-4" />Back</Link></Button>}
      />
      <main className="max-w-3xl space-y-6 p-4 md:p-6">
        <ErrorBanner message={error} />

        {canManage ? (
          <section className="rounded-md border bg-card p-4">
            <h2 className="text-sm font-semibold">Invite a member</h2>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="min-w-56 flex-1 space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input id="invite-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@firm.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as "member" | "admin" | "viewer")}>
                  <SelectTrigger id="invite-role" className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVITE_ROLES.map((role) => (<SelectItem key={role} value={role}>{role}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="button" onClick={() => void invite()} disabled={inviteMutation.isPending || !inviteEmail.trim()}>
                {inviteMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}Invite
              </Button>
            </div>
          </section>
        ) : null}

        <section className="rounded-md border bg-card p-4">
          <h2 className="text-sm font-semibold">Members</h2>
          <RouteErrorBoundary fallback={(boundaryError) => <ErrorBanner message={routeErrorMessage(boundaryError, "Could not load members.")} className="mt-3" />} resetKey={orgId}>
            <Suspense fallback={<OrgMembersSectionSkeleton />}>
              <OrgMembersList
                orgId={orgId}
                canManage={canManage}
                removePending={removeMutation.isPending}
                onChangeRole={changeRole}
                onRemove={remove}
              />
            </Suspense>
          </RouteErrorBoundary>
        </section>
      </main>
    </div>
  );
}

function OrgMembersList({
  orgId,
  canManage,
  removePending,
  onChangeRole,
  onRemove,
}: {
  orgId: string;
  canManage: boolean;
  removePending: boolean;
  onChangeRole: (membershipId: string, role: OrgRole) => Promise<void>;
  onRemove: (membershipId: string) => Promise<void>;
}) {
  const { data: members } = useSuspenseOrgMembers(orgId);

  return (
    <div className="mt-3 divide-y">
      {members.map((member: OrgMembershipRow) => (
        <div key={member.id} className="flex flex-wrap items-center gap-3 py-3">
          <div className="min-w-48 flex-1">
            <p className="truncate text-sm font-medium">{member.invited_email || member.user_id || "Unknown"}</p>
            <p className="text-xs text-muted-foreground">{member.status === "invited" ? "Invited" : member.status}</p>
          </div>
          {canManage && member.status !== "invited" ? (
            <Select value={member.role} onValueChange={(value) => void onChangeRole(member.id, value as OrgRole)}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_ROLES.map((role) => (<SelectItem key={role} value={role}>{role}</SelectItem>))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm text-muted-foreground">{member.role}</span>
          )}
          {canManage ? (
            <Button type="button" variant="ghost" size="icon" aria-label="Remove member" onClick={() => void onRemove(member.id)} disabled={removePending}>
              <Trash2 className="size-4" />
            </Button>
          ) : null}
        </div>
      ))}
      {members.length === 0 ? <p className="py-3 text-sm text-muted-foreground">No members yet.</p> : null}
    </div>
  );
}
